import React, { useState, useEffect, useRef, useCallback } from 'react';
import { bannerAds, appSettings } from '../lib/supabase';
import { trackAdImpression, trackAdClick } from '../lib/analytics';

// ==================== RESPONSIVE HOOK ====================
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    // Use matchMedia for better performance
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', (e) => setIsMobile(e.matches));
    } else {
      // Fallback for older browsers
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', (e) => setIsMobile(e.matches));
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [breakpoint]);

  return isMobile;
};

// ==================== ASPECT RATIOS BY PLACEMENT ====================
const PLACEMENT_CONFIG = {
  top: {
    desktop: { width: 728, height: 90 },
    mobile: { width: 320, height: 50 },
    maxWidth: 'max-w-3xl',
    aspectDesktop: 'aspect-[728/90]',
    aspectMobile: 'aspect-[320/50]'
  },
  bottom: {
    desktop: { width: 728, height: 90 },
    mobile: { width: 320, height: 50 },
    maxWidth: 'max-w-3xl',
    aspectDesktop: 'aspect-[728/90]',
    aspectMobile: 'aspect-[320/50]'
  },
  sidebar: {
    desktop: { width: 300, height: 250 },
    mobile: { width: 300, height: 250 },
    maxWidth: 'max-w-[300px]',
    aspectDesktop: 'aspect-[300/250]',
    aspectMobile: 'aspect-[300/250]'
  },
  inline: {
    desktop: { width: 468, height: 60 },
    mobile: { width: 320, height: 50 },
    maxWidth: 'max-w-2xl',
    aspectDesktop: 'aspect-[468/60]',
    aspectMobile: 'aspect-[320/50]'
  },
  inline1: {
    desktop: { width: 468, height: 60 },
    mobile: { width: 320, height: 50 },
    maxWidth: 'max-w-2xl',
    aspectDesktop: 'aspect-[468/60]',
    aspectMobile: 'aspect-[320/50]'
  },
  inline2: {
    desktop: { width: 468, height: 60 },
    mobile: { width: 320, height: 50 },
    maxWidth: 'max-w-2xl',
    aspectDesktop: 'aspect-[468/60]',
    aspectMobile: 'aspect-[320/50]'
  },
  inline3: {
    desktop: { width: 468, height: 60 },
    mobile: { width: 320, height: 50 },
    maxWidth: 'max-w-2xl',
    aspectDesktop: 'aspect-[468/60]',
    aspectMobile: 'aspect-[320/50]'
  },
  'mid-content': {
    desktop: { width: 468, height: 60 },
    mobile: { width: 320, height: 50 },
    maxWidth: 'max-w-2xl',
    aspectDesktop: 'aspect-[468/60]',
    aspectMobile: 'aspect-[320/50]'
  }
};

// Simple cache to avoid repeated failed requests
let _adsEnabledCache = null;
let _adsEnabledCacheTime = 0;
const _adDataCache = {};
const CACHE_TTL = 60000; // 1 minute cache

// ==================== BANNER AD COMPONENT ====================
export const BannerAd = ({ page = 'home', placement = 'top', className = '' }) => {
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adsEnabled, setAdsEnabled] = useState(false);
  const impressionTracked = useRef(false);
  const adRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    loadAd();
  }, [page, placement]);

  // Track impression when ad becomes visible
  useEffect(() => {
    if (!ad || impressionTracked.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          trackAdImpression(ad.id, ad.name, placement, page);
          impressionTracked.current = true;
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (adRef.current) {
      observer.observe(adRef.current);
    }

    return () => observer.disconnect();
  }, [ad, placement, page]);

  const loadAd = async () => {
    setLoading(true);
    try {
      // Check cache for ads_enabled setting
      const now = Date.now();
      let isEnabled;
      if (_adsEnabledCache !== null && (now - _adsEnabledCacheTime) < CACHE_TTL) {
        isEnabled = _adsEnabledCache;
      } else {
        const enabledResult = await appSettings.get('ads_enabled');
        const enabledValue = String(enabledResult?.data || '').replace(/^"|"$/g, '');
        isEnabled = enabledValue === 'true';
        _adsEnabledCache = isEnabled;
        _adsEnabledCacheTime = now;
      }

      if (!isEnabled) {
        setAdsEnabled(false);
        setAd(null);
        setLoading(false);
        return;
      }

      setAdsEnabled(true);

      // Check cache for ad data
      const cacheKey = `${page}`;
      if (_adDataCache[cacheKey] && (now - _adDataCache[cacheKey].time) < CACHE_TTL) {
        const matchingAd = _adDataCache[cacheKey].data?.find(a => a.placement === placement);
        setAd(matchingAd || null);
      } else {
        const { data } = await bannerAds.getActive(page);
        _adDataCache[cacheKey] = { data, time: now };
        const matchingAd = data?.find(a => a.placement === placement);
        setAd(matchingAd || null);
      }
    } catch (error) {
      // Silently handle - table may not exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleClick = useCallback(() => {
    if (!ad) return;
    trackAdClick(ad.id, ad.name, placement, page, ad.link_url);
  }, [ad, placement, page]);

  if (loading || !ad) return null;

  const config = PLACEMENT_CONFIG[placement] || PLACEMENT_CONFIG.inline;
  const hasMobileImage = ad.image_url_mobile && ad.image_url_mobile !== ad.image_url;

  return (
    <div
      ref={adRef}
      className={`banner-ad w-full ${config.maxWidth} mx-auto px-2 md:px-0 ${className}`}
    >
      <a
        href={ad.link_url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={handleClick}
        className="block relative group"
      >
        {/* Responsive image container with proper aspect ratio */}
        <div className={`relative overflow-hidden rounded-lg shadow-md ${isMobile ? config.aspectMobile : config.aspectDesktop}`}>
          {hasMobileImage ? (
            // Use picture element for responsive images when mobile image is available
            <picture>
              <source
                media="(max-width: 767px)"
                srcSet={ad.image_url_mobile}
              />
              <source
                media="(min-width: 768px)"
                srcSet={ad.image_url}
              />
              <img
                src={ad.image_url}
                alt={ad.alt_text || ad.name || 'Advertisement'}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                loading="lazy"
              />
            </picture>
          ) : (
            // Single image that scales responsively
            <img
              src={ad.image_url}
              alt={ad.alt_text || ad.name || 'Advertisement'}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              loading="lazy"
            />
          )}
        </div>

        {/* Ad label */}
        <span className="absolute bottom-2 right-2 text-[10px] text-gray-500 bg-white/90 px-1.5 py-0.5 rounded font-medium">
          Ad
        </span>
      </a>
    </div>
  );
};

// ==================== TOP BANNER WRAPPER ====================
export const TopBanner = ({ page }) => (
  <div className="mb-6">
    <BannerAd page={page} placement="top" />
  </div>
);

// ==================== BOTTOM BANNER WRAPPER ====================
export const BottomBanner = ({ page }) => (
  <div className="mt-6">
    <BannerAd page={page} placement="bottom" />
  </div>
);

// ==================== INLINE BANNER WRAPPERS (for between-content placement) ====================
// Use these for ads between content sections - they have appropriate spacing
export const InlineBanner = ({ page, position = 1, className = '' }) => (
  <div className={`my-6 ${className}`}>
    <BannerAd page={page} placement={`inline${position}`} />
  </div>
);

// Specific inline positions for clarity
export const InlineBanner1 = ({ page }) => <InlineBanner page={page} position={1} />;
export const InlineBanner2 = ({ page }) => <InlineBanner page={page} position={2} />;
export const InlineBanner3 = ({ page }) => <InlineBanner page={page} position={3} />;

// Mid-content banner (alias for semantic clarity)
export const MidContentBanner = ({ page }) => (
  <div className="my-6">
    <BannerAd page={page} placement="mid-content" />
  </div>
);

// ==================== BANNER AD PLACEHOLDER (for layout/demo) ====================
export const BannerPlaceholder = ({ placement = 'top' }) => {
  const isMobile = useIsMobile();
  const config = PLACEMENT_CONFIG[placement] || PLACEMENT_CONFIG.top;
  const size = isMobile ? config.mobile : config.desktop;

  return (
    <div className={`w-full ${config.maxWidth} mx-auto px-2 md:px-0`}>
      <div
        className={`relative overflow-hidden rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 ${isMobile ? config.aspectMobile : config.aspectDesktop}`}
      >
        <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
          <span>Ad Space ({size.width}x{size.height})</span>
        </div>
      </div>
    </div>
  );
};

export default BannerAd;
